import { alpha, createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import components from './Components';
import type { DesignVariant } from '@shared/designVariants/catalog';

/** Tokens measured from the V1–V5 source layouts; Main keeps its own theme. */
export const createDesignVariantTheme = (parent: Theme, variant: DesignVariant): Theme => {
  if (variant === 'main') return parent;
  const dark = parent.palette.mode === 'dark';
  const mono = variant === 'v4';
  const missions = variant === 'v5';
  const compact = ['v3', 'v4', 'v5'].includes(variant);
  const primary = mono
    ? dark
      ? '#eeeeee'
      : '#171717'
    : missions
      ? dark
        ? '#67c7d2'
        : '#127c91'
      : parent.palette.primary.main;
  const paper = dark
    ? mono
      ? '#202020'
      : missions
        ? '#1e2326'
        : parent.palette.background.paper
    : '#ffffff';
  const page = dark
    ? mono
      ? '#171717'
      : missions
        ? '#15191b'
        : parent.palette.background.default
    : mono
      ? '#f8f8f8'
      : missions
        ? '#f7f9fa'
        : variant === 'v2'
          ? '#edf1f7'
          : variant === 'v1'
            ? '#edf1f7'
            : '#f5f6fb';
  const fontFamily = mono
    ? '"Helvetica Neue", Helvetica, Arial, sans-serif'
    : missions
      ? 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif'
      : parent.typography.fontFamily;
  const typography = Object.fromEntries(
    Object.entries(parent.typography)
      .filter(([, value]) => typeof value === 'object')
      .map(([name, value]) => [name, { ...value, fontFamily }]),
  );
  const options: ThemeOptions = {
    palette: {
      primary: {
        main: primary,
        alphaChannel: alpha(primary, 0.08),
        light: mono
          ? dark
            ? '#333333'
            : '#f2f2f2'
          : missions
            ? dark
              ? '#15373b'
              : '#e8f5f5'
            : parent.palette.primary.light,
        dark: mono
          ? dark
            ? '#ffffff'
            : '#000000'
          : missions
            ? dark
              ? '#8adce2'
              : '#0b5969'
            : parent.palette.primary.dark,
        contrastText: mono && dark ? '#171717' : missions && dark ? '#112b30' : '#ffffff',
      },
      background: { default: page, paper },
      ...{
        text: {
          primary: dark ? '#f2f5f5' : '#1a1d1f',
          secondary: dark ? '#a5b0b5' : '#727c82',
        },
        divider: dark ? '#3b4145' : '#e8ecee',
        surface: {
          page,
          card: paper,
          raised: dark ? '#292d30' : '#f6f8f9',
          sunken: page,
          overlay: parent.palette.surface.overlay,
        },
        border: {
          default: dark ? '#44494c' : '#e2e8eb',
          subtle: dark ? '#333a3e' : '#edf0f2',
          strong: dark ? '#63737a' : '#c6d0d5',
        },
      },
    },
    typography: {
      ...typography,
      fontFamily,
      h3: { fontFamily, fontSize: 28, lineHeight: 1.3, fontWeight: 650, letterSpacing: '-.9px' },
      h4: { fontFamily, fontSize: 22, lineHeight: 1.4, fontWeight: 600, letterSpacing: '-.4px' },
      body1: { fontFamily, fontSize: 14, lineHeight: 1.7 },
      body2: { fontFamily, fontSize: 13, lineHeight: 1.65 },
      caption: { fontFamily, fontSize: 11, lineHeight: 1.6 },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 8, fontSize: 13, backgroundColor: paper } },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: mono ? 11 : 8,
            fontFamily: 'inherit',
            boxShadow: 'none',
            ...(compact ? { minHeight: 36, fontSize: 13 } : {}),
          },
          textPrimary: {
            backgroundColor: 'transparent',
            color: primary,
            '&:hover': { backgroundColor: alpha(primary, 0.08), color: primary },
          },
          containedPrimary: {
            backgroundImage: 'none',
            ...(variant === 'v3'
              ? {
                  backgroundColor: dark ? '#eeeeee' : '#283247',
                  color: dark ? '#171717' : '#ffffff',
                }
              : {}),
            boxShadow: 'none',
            '&:hover': { backgroundImage: 'none', boxShadow: 'none' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', boxShadow: 'none' },
          outlined: { borderColor: dark ? '#343d41' : '#e2e6ed' },
          rounded: { borderRadius: variant === 'v2' ? 20 : mono ? 14 : 12 },
        },
      },
      ...(compact
        ? {
            MuiTableCell: {
              styleOverrides: {
                root: { fontSize: 12, padding: '16px 18px' },
                head: {
                  fontSize: 11,
                  fontWeight: 500,
                  backgroundColor: dark ? '#262d30' : '#f8fafb',
                  color: dark ? '#a5b4bb' : '#7b858c',
                },
              },
            },
            MuiChip: { styleOverrides: { root: { height: 26, borderRadius: 6, fontSize: 11 } } },
          }
        : {}),
    },
  };
  const theme = createTheme(parent, options);
  // Parent component styles contain resolved Main colors. Rebuild them from these tokens.
  theme.components = deepmerge(components(theme), options.components ?? {}) as Theme['components'];
  return theme;
};
